import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { StoreErrorHandler } from '@zero/shared/util-global-state';
import { Asset, MultiExanteResponse, SavingsPlan, TopInstrument, TopInstruments } from '@zero/shared/util-models';
import { AssetSearchService } from '@zero/shared/util-services';
import { catchError, concatMap, filter, map, of, switchMap, tap } from 'rxjs';
import { MultiSavingsPlan } from '../entities/multi-savings-plan.entity';
import { SavingsPlanEdit } from '../entities/savings-plan-edit.entity';
import { SavingsPlanInfrastructure } from '../infrastructure/savings-plan.infrastructure';
import { savingPlansActions, savingPlansApiActions } from './saving-plans.actions';
import { savingPlansFeature } from './saving-plans.reducers';
import { selectSavingPlanById } from './saving-plans.selectors';

@Injectable({ providedIn: 'root' })
export class SavingPlansEffects {
  readonly #actions = inject(Actions);
  readonly #store = inject(Store);
  readonly #savingsPlanInfrastructure = inject(SavingsPlanInfrastructure);
  readonly #storeErrorHandler = inject(StoreErrorHandler);
  readonly #assetSearchService = inject(AssetSearchService);

  public loadSavingsPlansOverview$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.loadSavingsPlansOverview),
      concatLatestFrom(() => this.#store.select(savingPlansFeature.selectSavingsPlansOverviewLoadingState)),
      filter(([{ force }, loading]) => force || loading === 'INITIAL'),
      map(() => savingPlansApiActions.loadSavingsPlansOverviewFromAPI()),
    );
  });

  public loadSavingsPlansOverviewFromAPI$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansApiActions.loadSavingsPlansOverviewFromAPI, savingPlansApiActions.cancelSavingsPlanSuccess),
      switchMap(() =>
        this.#savingsPlanInfrastructure.getSavingsPlansOverview().pipe(
          map((response) => {
            return {
              ...response,
              savingsPlans: response.savingsPlans
                .filter((plan: SavingsPlan) => plan.status !== 'deleted')
                .sort((plan1: SavingsPlan, plan2: SavingsPlan) => +plan1.createdDate - +plan2.createdDate),
            };
          }),
          map((savingsPlansOverview) => savingPlansApiActions.loadSavingsPlansOverviewSuccess({ savingsPlansOverview })),
          catchError((error) => of(savingPlansApiActions.loadSavingsPlansOverviewFailure({ error }))),
        ),
      ),
    );
  });

  public cancelSavingsPlan$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.cancelSavingsPlan),
      concatLatestFrom(({ id }) => this.#store.select(selectSavingPlanById(id))),
      switchMap(([, savingPlan]) =>
        this.#savingsPlanInfrastructure.deleteSavingsPlan(savingPlan).pipe(
          map(() => savingPlansApiActions.cancelSavingsPlanSuccess()),
          catchError((error) => of(savingPlansApiActions.cancelSavingsPlanFailure({ error }))),
        ),
      ),
    );
  });

  public loadMostChosenSavingPlans$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.loadMostChosenSavingPlans),
      concatLatestFrom(() => this.#store.select(savingPlansFeature.selectMostChosenSavingPlansLoadingState)),
      filter(([{ force }, loading]) => force || loading === 'INITIAL'),
      map(([{ itemsCount }]) => savingPlansApiActions.loadMostChosenSavingPlansFromAPI({ itemsCount })),
    );
  });

  public loadMostChosenSavingPlansFromAPI$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansApiActions.loadMostChosenSavingPlansFromAPI),
      switchMap(({ itemsCount }) =>
        this.#savingsPlanInfrastructure.getMostChosen(itemsCount).pipe(
          map((mostChosenSavingPlans) => {
            const map: TopInstruments = {};
            mostChosenSavingPlans.forEach((item: TopInstrument): void => {
              if (map[item.type]) {
                map[item.type].push(item);
              } else {
                map[item.type] = [item];
              }
            });
            return map;
          }),
          map((mostChosenSavingPlans) => savingPlansApiActions.loadMostChosenSavingPlansSuccess({ mostChosenSavingPlans })),
          catchError((error) => of(savingPlansApiActions.loadMostChosenSavingPlansFailure({ error }))),
        ),
      ),
    );
  });

  public loadExecDates$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.loadExecDates),
      concatLatestFrom(() => this.#store.select(savingPlansFeature.selectExecDatesLoadingState)),
      filter(([{ force }, loading]) => force || loading === 'INITIAL'),
      map(() => savingPlansApiActions.loadExecDatesFromAPI()),
    );
  });

  public loadOptions$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.loadOptions),
      switchMap(() =>
        this.#savingsPlanInfrastructure.getOptions().pipe(
          map((options) => savingPlansApiActions.loadOptionsSuccess({ options })),
          catchError((error) => of(savingPlansApiActions.loadOptionsFailure({ error }))),
        ),
      ),
    );
  });

  public loadExecDatesFromAPI$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansApiActions.loadExecDatesFromAPI),
      switchMap(() =>
        this.#savingsPlanInfrastructure.getExecDates().pipe(
          map((execDates) => savingPlansApiActions.loadExecDatesSuccess({ execDates })),
          catchError((error) => of(savingPlansApiActions.loadExecDatesFailure({ error }))),
        ),
      ),
    );
  });

  public loadSavingsPlanSubjects$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.loadSavingsPlanSubjects),
      concatLatestFrom(() => this.#store.select(savingPlansFeature.selectSavingsPlanSubjectsLoadingState)),
      filter(([{ force }, loading]) => force || loading === 'INITIAL'),
      map(() => savingPlansApiActions.loadSavingsPlanSubjectsFromAPI()),
    );
  });

  public loadSavingsPlanSubjectsFromAPI$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansApiActions.loadSavingsPlanSubjectsFromAPI),
      switchMap(() =>
        this.#savingsPlanInfrastructure.getSubjects().pipe(
          map((savingsPlanSubjects) => savingPlansApiActions.loadSavingsPlanSubjectsSuccess({ savingsPlanSubjects })),
          catchError((error) => of(savingPlansApiActions.loadSavingsPlanSubjectsFailure({ error }))),
        ),
      ),
    );
  });

  public updateAmount$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.updateAmount, savingPlansActions.updateAmountSilently),
      concatLatestFrom(() => this.#store.select(savingPlansFeature.selectLastChangedSavingsPlan)),
      map(([, savingsPlan]) => {
        const { amount } = savingsPlan;
        const errorPrefix = `savings-plan.add-savings-plan.errors`;
        if (!amount) {
          return savingPlansActions.setError({ savingsPlan, error: `${errorPrefix}.missing` });
        }

        if (amount < 1) {
          return savingPlansActions.setError({ savingsPlan, error: `${errorPrefix}.min` });
        }

        return savingPlansActions.setError({ savingsPlan, error: null });
      }),
    );
  });

  public updateExistingSavingsPlanAmount$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.updateExistingSavingsPlanAmount),
      map(({ amount }) => {
        const errorPrefix = `savings-plan.add-savings-plan.errors`;
        if (!amount) {
          return savingPlansActions.setExistingSavingsPlanError({ error: `${errorPrefix}.missing`, amount });
        }

        if (amount < 1) {
          return savingPlansActions.setExistingSavingsPlanError({ error: `${errorPrefix}.min`, amount });
        }

        return savingPlansActions.setExistingSavingsPlanError({ error: null, amount });
      }),
    );
  });

  public addMultiSavingsPlans$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.addAssetsToForm),
      switchMap(({ savingsPlans }) =>
        this.#assetSearchService.loadAssetsByIsins(savingsPlans.map(({ isin }) => isin)).pipe(
          map((assets: Asset[]) =>
            assets?.length
              ? savingPlansApiActions.addAssetsToFormSuccess({
                  savingsPlans: this.#addAssets(savingsPlans, assets),
                })
              : savingPlansApiActions.addAssetsToFormFailure({
                  error: new HttpErrorResponse({ status: 404, statusText: 'No Assets Found' }),
                }),
          ),
          catchError((error) => of(savingPlansApiActions.addAssetsToFormFailure(error))),
        ),
      ),
    );
  });

  public changeTotalAmount$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.changeTotalAmount),
      concatLatestFrom(() => this.#store.select(savingPlansFeature.selectMultiSavingsPlans)),
      concatMap(([, savingPlans]) => savingPlans.map((savingsPlan) => savingPlansActions.updateAmountSilently({ savingsPlan }))),
    );
  });

  public createSavingsPlans$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.createSavingsPlans),
      concatLatestFrom(() => [
        this.#store.select(savingPlansFeature.selectMultiSavingsPlans),
        this.#store.select(savingPlansFeature.selectExecDate),
        this.#store.select(savingPlansFeature.selectInterval),
        this.#store.select(savingPlansFeature.selectSelectedDynamization),
      ]),
      switchMap(([, savingPlans, execDate, interval, dynamization]) =>
        this.#savingsPlanInfrastructure.createSavingsPlans(savingPlans, execDate, interval, dynamization).pipe(
          map(() => savingPlansApiActions.createSavingsPlansSuccess()),
          catchError((error) => of(savingPlansApiActions.createSavingsPlansFailure(error))),
        ),
      ),
    );
  });

  public updateSavingsPlan$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.updateSavingsPlan),
      concatLatestFrom(() => [
        this.#store.select(savingPlansFeature.selectSavingsPlanToUpdate),
        this.#store.select(savingPlansFeature.selectExecDate),
        this.#store.select(savingPlansFeature.selectInterval),
        this.#store.select(savingPlansFeature.selectSelectedDynamization),
      ]),
      switchMap(([, savingPlan, execDate, interval, dynamization]) =>
        this.#savingsPlanInfrastructure.updateSavingsPlan(savingPlan, execDate, interval, dynamization).pipe(
          map(() => savingPlansApiActions.updateSavingsPlansSuccess()),
          catchError((error) => of(savingPlansApiActions.updateSavingsPlansFailure(error))),
        ),
      ),
    );
  });

  public loadMultiExante$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.loadExante),
      concatLatestFrom(() => [
        this.#store.select(savingPlansFeature.selectMultiSavingsPlans),
        this.#store.select(savingPlansFeature.selectSavingsPlanToUpdate),
      ]),
      switchMap(([, savingPlans, savingsPlanToUpdate]) =>
        this.#savingsPlanInfrastructure.loadExante(savingsPlanToUpdate ? [savingsPlanToUpdate.isin] : savingPlans.map((i) => i.isin)).pipe(
          map((multiExante: MultiExanteResponse) => savingPlansApiActions.loadExanteSuccess({ multiExante })),
          catchError((error) => of(savingPlansApiActions.loadExanteFailure(error))),
        ),
      ),
    );
  });

  public loadExistingSavingsPlan$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.loadExistingSavingsPlan),
      concatLatestFrom(() => this.#store.select(savingPlansFeature.selectSavingsPlansOverview)),
      switchMap(([{ id }, savingPlans]) => {
        return this.#savingsPlanInfrastructure.loadExistingSavingsPlan(savingPlans?.savingsPlans.find((i) => i.id === id)).pipe(
          map((savingsPlan: SavingsPlanEdit) => savingPlansApiActions.loadExistingSavingsPlanSuccess({ savingsPlan })),
          catchError((error) => of(savingPlansApiActions.loadExistingSavingsPlanFailure(error))),
        );
      }),
    );
  });

  public loadOnboardingSavingsPlanInfos$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.loadOnboardingSavingsPlanInfos),
      concatLatestFrom(() => this.#store.select(savingPlansFeature.selectOnboardingSavingsPlanIsin)),
      filter(([, isin]) => !!isin),
      concatMap(([, isin]) => {
        return this.#assetSearchService.loadAssetByIsin(isin).pipe(
          map((asset: Asset) => savingPlansApiActions.loadOnboardingSavingsPlanInfosSuccess({ asset })),
          catchError((error) => of(savingPlansApiActions.loadOnboardingSavingsPlanInfosFailure(error))),
        );
      }),
    );
  });

  public loadOnboardingSavingsPlanExante$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansApiActions.loadOnboardingSavingsPlanInfosSuccess),
      concatLatestFrom(() => this.#store.select(savingPlansFeature.selectOnboardingSavingsPlanIsin)),
      concatMap(([, isin]) => {
        return this.#savingsPlanInfrastructure.loadExante([isin]).pipe(
          map((exante: MultiExanteResponse) => savingPlansApiActions.loadOnboardingSavingsPlanExanteSuccess({ exante })),
          catchError((error) => of(savingPlansApiActions.loadOnboardingSavingsPlanExanteFailure(error))),
        );
      }),
    );
  });

  public submitOnboardingSavingsPlan$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.submitOnboardingSavingsPlan),
      concatLatestFrom(() => this.#store.select(savingPlansFeature.selectOnboardingSavingsPlanIsin)),
      concatLatestFrom(() => this.#store.select(savingPlansFeature.selectOnboardingSavingsPlanAmount)),
      concatMap(([[, isin], amount]) => {
        return this.#savingsPlanInfrastructure.submitOnboardingSavingsPlan(amount, isin).pipe(
          map(() => savingPlansApiActions.submitOnboardingSavingsPlanSuccess()),
          catchError((error) => of(savingPlansApiActions.submitOnboardingSavingsPlanFailure(error))),
        );
      }),
    );
  });

  public loadOnboardingSavingsPlans$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.loadOnboardingSavingsPlans),
      switchMap(() => {
        return this.#savingsPlanInfrastructure.loadOnboardingSavingsPlans().pipe(
          map((response) => savingPlansApiActions.loadOnboardingSavingsPlansSuccess({ savingsPlans: response })),
          catchError((error) => of(savingPlansApiActions.loadOnboardingSavingsPlansFailure(error))),
        );
      }),
    );
  });

  public deleteOnboardingSavingsPlan$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(savingPlansActions.deleteOnboardingSavingsPlan),
      switchMap(({ id }) =>
        this.#savingsPlanInfrastructure.deleteOnboardingSavingsPlan(id).pipe(
          map(() => savingPlansApiActions.deleteOnboardingSavingsPlanSuccess()),
          catchError((error) => of(savingPlansApiActions.deleteOnboardingSavingsPlanFailure({ error }))),
        ),
      ),
    );
  });

  public handleBackendErrors$ = createEffect(
    () => {
      return this.#actions.pipe(
        ofType(
          savingPlansApiActions.loadSavingsPlansOverviewFailure,
          savingPlansApiActions.cancelSavingsPlanFailure,
          savingPlansApiActions.loadMostChosenSavingPlansFailure,
          savingPlansApiActions.loadExecDatesFailure,
          savingPlansApiActions.loadSavingsPlanSubjectsFailure,
          savingPlansApiActions.addAssetsToFormFailure,
          savingPlansApiActions.loadExistingSavingsPlanFailure,
          savingPlansApiActions.loadOnboardingSavingsPlanInfosFailure,
          savingPlansApiActions.loadOnboardingSavingsPlanExanteFailure,
          savingPlansApiActions.submitOnboardingSavingsPlanFailure,
          savingPlansApiActions.loadOnboardingSavingsPlansFailure,
          savingPlansApiActions.deleteOnboardingSavingsPlanFailure,
        ),
        tap(({ error }) => this.#storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );

  readonly #addAssets = (savingsPlans: MultiSavingsPlan[], assets: Asset[]) =>
    savingsPlans.map((i) => ({ ...i, asset: assets.find(({ isin }) => i.isin === isin) }));
}
